/**
 * Alumni Self-Service Portal — Phase 4.
 * Login with admission_number + (date_of_birth OR last_name).
 * Dashboard tabs: My Profile, My Requests, My Chapters, My Card.
 *
 * Session stored under localStorage key `fwbc-alumni-session`.
 */
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { UserCheck, LogOut, IdCard, FileText, Globe2, Loader2, Plus, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const KEY = 'fwbc-alumni-session';
const loadSession = () => { try { return JSON.parse(localStorage.getItem(KEY) || 'null'); } catch { return null; } };
const saveSession = (s) => localStorage.setItem(KEY, JSON.stringify(s));
const clearSession = () => localStorage.removeItem(KEY);

const api = async (path, opts = {}) => {
  const s = loadSession();
  const token = s?.access_token;
  const res = await fetch(`${API}/api/school${path}`, {
    ...opts,
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', ...opts.headers },
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Request failed'); }
  return res.json();
};

export default function AlumniPortalPage() {
  const [params] = useSearchParams();
  const [session, setSession] = useState(loadSession());
  const tenantIdPreset = params.get('tenant') || '';

  if (!session) {
    return <LoginView tenantIdPreset={tenantIdPreset} onLogin={(s) => { saveSession(s); setSession(s); }} />;
  }
  return <PortalView session={session} onLogout={() => { clearSession(); setSession(null); }} />;
}

function LoginView({ tenantIdPreset, onLogin }) {
  const [form, setForm] = useState({ tenant_id: tenantIdPreset, admission_number: '', date_of_birth: '', last_name: '' });
  const mut = useMutation({
    mutationFn: async () => {
      const body = { tenant_id: form.tenant_id, admission_number: form.admission_number };
      if (form.date_of_birth) body.date_of_birth = form.date_of_birth;
      if (form.last_name) body.last_name = form.last_name;
      const res = await fetch(`${API}/api/public/alumni/auth/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Login failed');
      return d;
    },
    onSuccess: onLogin,
    onError: e => toast.error(e.message),
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-950 via-slate-900 to-indigo-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-2xl" data-testid="alumni-login-card">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2"><UserCheck className="h-5 w-5 text-indigo-500" />Alumni Portal</CardTitle>
          <CardDescription>Welcome back. Log in with your admission number and date of birth or last name as on record.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div><Label className="text-xs">School / Tenant ID</Label><Input data-testid="alumni-login-tenant" value={form.tenant_id} onChange={e => setForm({ ...form, tenant_id: e.target.value })} placeholder="School tenant id" /></div>
          <div><Label className="text-xs">Admission / Student Number</Label><Input data-testid="alumni-login-admission" value={form.admission_number} onChange={e => setForm({ ...form, admission_number: e.target.value })} placeholder="FWBC-1010" /></div>
          <div><Label className="text-xs">Date of Birth (YYYY-MM-DD)</Label><Input data-testid="alumni-login-dob" type="date" value={form.date_of_birth} onChange={e => setForm({ ...form, date_of_birth: e.target.value })} /></div>
          <p className="text-xs text-slate-400 text-center">— or —</p>
          <div><Label className="text-xs">Last Name</Label><Input data-testid="alumni-login-lastname" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} placeholder="As on school record" /></div>
          <Button className="w-full" onClick={() => mut.mutate()} disabled={!form.tenant_id || !form.admission_number || (!form.date_of_birth && !form.last_name) || mut.isPending} data-testid="alumni-login-btn">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Sign in
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function PortalView({ session, onLogout }) {
  const [tab, setTab] = useState('profile');
  return (
    <div className="min-h-screen bg-slate-50" data-testid="alumni-portal">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold flex items-center gap-2"><UserCheck className="h-4 w-4 text-indigo-500" />Welcome, {session.name}</p>
            <p className="text-[11px] text-slate-500">Alumni portal</p>
          </div>
          <Button size="sm" variant="outline" onClick={onLogout} data-testid="alumni-portal-logout"><LogOut className="h-3.5 w-3.5 mr-1" />Sign out</Button>
        </div>
      </header>
      <main className="max-w-5xl mx-auto p-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="profile" data-testid="portal-tab-profile">My Profile</TabsTrigger>
            <TabsTrigger value="requests" data-testid="portal-tab-requests">Requests</TabsTrigger>
            <TabsTrigger value="chapters" data-testid="portal-tab-chapters">Chapters</TabsTrigger>
            <TabsTrigger value="card" data-testid="portal-tab-card">Digital Card</TabsTrigger>
          </TabsList>
          <TabsContent value="profile" className="mt-4"><MyProfile /></TabsContent>
          <TabsContent value="requests" className="mt-4"><MyRequests /></TabsContent>
          <TabsContent value="chapters" className="mt-4"><MyChapters /></TabsContent>
          <TabsContent value="card" className="mt-4"><MyCard session={session} /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function MyProfile() {
  const qc = useQueryClient();
  const { data: profile, isLoading } = useQuery({ queryKey: ['me-profile'], queryFn: () => api('/alumni/me/profile') });
  const [form, setForm] = useState({});
  React.useEffect(() => { if (profile) setForm(profile); }, [profile]);
  const mut = useMutation({
    mutationFn: () => api('/alumni/me/profile', { method: 'PUT', body: JSON.stringify({
      phone: form.phone, email: form.email, country: form.country, city: form.city,
      employment_status: form.employment_status, employer: form.employer, position: form.position,
      ministry_placement: form.ministry_placement, profile_summary: form.profile_summary,
      available_for_mentorship: form.available_for_mentorship, willing_to_support: form.willing_to_support,
      areas_of_expertise: form.areas_of_expertise,
    }) }),
    onSuccess: () => { toast.success('Profile updated'); qc.invalidateQueries({ queryKey: ['me-profile'] }); },
    onError: e => toast.error(e.message),
  });
  if (isLoading) return <div className="text-center py-12"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;
  if (!profile) return <p className="text-slate-400">Profile not found.</p>;
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">My Profile</CardTitle><CardDescription>Keep your details up to date so your school can stay in touch.</CardDescription></CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Full Name</Label><Input value={form.name || ''} disabled /></div>
          <div><Label className="text-xs">Admission #</Label><Input value={form.admission_number || ''} disabled /></div>
          <div><Label className="text-xs">Phone</Label><Input value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="portal-profile-phone" /></div>
          <div><Label className="text-xs">Email</Label><Input value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
          <div><Label className="text-xs">Country</Label><Input value={form.country || ''} onChange={e => setForm({ ...form, country: e.target.value })} /></div>
          <div><Label className="text-xs">City</Label><Input value={form.city || ''} onChange={e => setForm({ ...form, city: e.target.value })} /></div>
          <div><Label className="text-xs">Employment</Label>
            <Select value={form.employment_status || ''} onValueChange={v => setForm({ ...form, employment_status: v })}>
              <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                {['employed', 'self_employed', 'ministry', 'student', 'seeking'].map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Position</Label><Input value={form.position || ''} onChange={e => setForm({ ...form, position: e.target.value })} /></div>
          <div><Label className="text-xs">Employer / Org</Label><Input value={form.employer || ''} onChange={e => setForm({ ...form, employer: e.target.value })} /></div>
          <div className="col-span-2"><Label className="text-xs">Ministry Placement</Label><Input value={form.ministry_placement || ''} onChange={e => setForm({ ...form, ministry_placement: e.target.value })} /></div>
          <div className="col-span-2"><Label className="text-xs">Profile Summary / Testimony</Label><Textarea rows={3} value={form.profile_summary || ''} onChange={e => setForm({ ...form, profile_summary: e.target.value })} /></div>
        </div>
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!form.available_for_mentorship} onChange={e => setForm({ ...form, available_for_mentorship: e.target.checked })} data-testid="portal-profile-mentor" />Available as a mentor</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.willing_to_support !== false} onChange={e => setForm({ ...form, willing_to_support: e.target.checked })} />Willing to support my school</label>
        </div>
        <Button onClick={() => mut.mutate()} disabled={mut.isPending} data-testid="portal-profile-save">{mut.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />}Save changes</Button>
      </CardContent>
    </Card>
  );
}

function MyRequests() {
  const qc = useQueryClient();
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ request_type: 'transcript', details: '' });
  const { data: requests = [] } = useQuery({ queryKey: ['me-requests'], queryFn: () => api('/alumni/me/requests') });
  const mut = useMutation({
    mutationFn: () => api('/alumni/me/requests', { method: 'POST', body: JSON.stringify({ ...form, alumni_id: 'self' }) }),
    onSuccess: () => { toast.success('Request submitted'); qc.invalidateQueries({ queryKey: ['me-requests'] }); setShow(false); },
    onError: e => toast.error(e.message),
  });
  const TYPES = [
    { value: 'transcript', label: 'Transcript' }, { value: 'certificate_reprint', label: 'Certificate Reprint' },
    { value: 'recommendation', label: 'Recommendation Letter' }, { value: 'reference', label: 'Reference Letter' },
    { value: 'verification_letter', label: 'Verification Letter' }, { value: 'ministry_endorsement', label: 'Ministry Endorsement' },
  ];
  const STATUS_COLOR = { submitted: 'bg-slate-100 text-slate-700', under_review: 'bg-amber-100 text-amber-800', approved: 'bg-blue-100 text-blue-700', paid: 'bg-indigo-100 text-indigo-700', completed: 'bg-emerald-100 text-emerald-800', delivered: 'bg-emerald-200 text-emerald-900', rejected: 'bg-red-100 text-red-700' };
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <div><CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4" />My Requests</CardTitle><CardDescription>Transcripts, letters, and certificate reprints.</CardDescription></div>
        <Button size="sm" onClick={() => setShow(true)} data-testid="portal-new-request"><Plus className="h-3.5 w-3.5 mr-1" />New Request</Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {requests.length === 0 && <p className="text-center text-slate-400 py-8 text-sm">No requests yet.</p>}
          {requests.map(r => (
            <div key={r.id} className="border rounded p-3 flex justify-between items-center" data-testid={`portal-request-${r.id}`}>
              <div>
                <p className="text-sm font-medium capitalize">{(r.request_type || '').replace(/_/g, ' ')}</p>
                <p className="text-xs text-slate-500">{r.details || '—'}</p>
                <p className="text-[10px] text-slate-400">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}</p>
              </div>
              <Badge className={STATUS_COLOR[r.status] || ''}>{(r.status || '').replace(/_/g, ' ')}</Badge>
            </div>
          ))}
        </div>
      </CardContent>
      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Request</DialogTitle><DialogDescription className="text-xs">Your school's registrar office will review and process it.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Type</Label>
              <Select value={form.request_type} onValueChange={v => setForm({ ...form, request_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Details</Label><Textarea rows={4} value={form.details} onChange={e => setForm({ ...form, details: e.target.value })} placeholder="Context, delivery address, deadline…" data-testid="portal-request-details" /></div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => mut.mutate()} disabled={mut.isPending} data-testid="portal-submit-request">{mut.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />}Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function MyChapters() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['me-chapters'], queryFn: () => api('/alumni/me/chapters') });
  const joinMut = useMutation({
    mutationFn: (cid) => api(`/alumni/me/chapters/${cid}/join`, { method: 'POST' }),
    onSuccess: () => { toast.success('Joined chapter'); qc.invalidateQueries({ queryKey: ['me-chapters'] }); },
    onError: e => toast.error(e.message),
  });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Globe2 className="h-4 w-4" />My Chapters</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {(data?.my_chapters || []).length === 0 && <p className="text-xs text-slate-400 text-center py-6">You haven't joined any chapter yet.</p>}
          {(data?.my_chapters || []).map(c => (
            <div key={c.id} className="border rounded p-2" data-testid={`portal-my-chapter-${c.id}`}>
              <p className="text-sm font-medium">{c.name}</p>
              <p className="text-[11px] text-slate-500">{[c.city, c.country].filter(Boolean).join(', ') || 'Global'}</p>
            </div>
          ))}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Available Chapters</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {(data?.available_chapters || []).length === 0 && <p className="text-xs text-slate-400 text-center py-6">You're in all active chapters already.</p>}
          {(data?.available_chapters || []).map(c => (
            <div key={c.id} className="border rounded p-2 flex justify-between items-center" data-testid={`portal-avail-chapter-${c.id}`}>
              <div>
                <p className="text-sm font-medium">{c.name}</p>
                <p className="text-[11px] text-slate-500">{[c.city, c.country].filter(Boolean).join(', ') || 'Global'}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => joinMut.mutate(c.id)} disabled={joinMut.isPending} data-testid={`portal-join-${c.id}`}>Join</Button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function MyCard({ session }) {
  const { data, isLoading } = useQuery({ queryKey: ['me-card'], queryFn: () => api('/alumni/me/card') });
  if (isLoading || !data) return <div className="text-center py-12"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;
  const verifyUrl = `${window.location.origin}${data.verify_path}`;
  const qrImg = `https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(verifyUrl)}`;
  return (
    <Card>
      <CardHeader><CardTitle className="text-base flex items-center gap-2"><IdCard className="h-4 w-4" />Your Digital Alumni Card</CardTitle><CardDescription>Share this with employers or scan for public verification.</CardDescription></CardHeader>
      <CardContent>
        <div className="max-w-md mx-auto rounded-xl overflow-hidden border shadow-lg" data-testid="my-alumni-card">
          <div className="p-4 text-white" style={{ background: `linear-gradient(135deg, ${data.institution_color}, ${data.institution_color}dd)` }}>
            <div className="flex items-center gap-3">
              {data.institution_logo && <img src={data.institution_logo} alt="logo" className="h-10 w-10 rounded bg-white/20 object-contain" />}
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-wider opacity-80">Alumni Card</p>
                <p className="text-sm font-bold">{data.institution}</p>
              </div>
              <Sparkles className="h-5 w-5 opacity-80" />
            </div>
          </div>
          <div className="p-4 bg-white">
            <div className="flex gap-3">
              <div className="h-16 w-16 rounded bg-slate-100 flex items-center justify-center text-slate-400 font-bold text-lg">
                {data.photo_url ? <img src={data.photo_url} alt="" className="h-full w-full object-cover rounded" /> : (data.name || '?').split(' ').map(n => n[0]).join('').slice(0, 2)}
              </div>
              <div className="flex-1">
                <p className="font-semibold text-slate-900">{data.name}</p>
                <p className="text-xs text-slate-500">{data.program || '—'} · Class of {data.graduation_year || '—'}</p>
                <p className="text-[11px] text-slate-400 mt-1"><strong>ID:</strong> {data.admission_number}</p>
                {(data.position || data.employer) && <p className="text-[11px] text-slate-500">{[data.position, data.employer].filter(Boolean).join(' · ')}</p>}
              </div>
            </div>
            <div className="mt-3 flex items-end justify-between">
              <div className="text-[9px] text-slate-400 max-w-[180px]">
                <p>Verify at:</p>
                <p className="break-all">{verifyUrl}</p>
              </div>
              <img src={qrImg} alt="QR" className="h-[100px] w-full max-w-[100px]" />
            </div>
          </div>
        </div>
        <div className="flex justify-center mt-4"><Button onClick={() => window.print()} data-testid="portal-print-card">Print / Save as PDF</Button></div>
      </CardContent>
    </Card>
  );
}
